import { VoicePlan, DEFAULT_VOICE_PLAN_CATEGORIES } from './VoicePlan';
import type { VoicePlanCategory } from './VoicePlan';
import { Subscription } from './Subscription';
import { PlaylistReference } from './Playlist';

// Get Dutch book entitlement key (for backward compatibility)

export type EntitlementStatus = "pending" | "approved" | "rejected";
export interface Entitlement {
    key: string;
    metadata: Record<string, any>;
    status: EntitlementStatus;
}
export const METADATA_KEYS = {
    SEEN_TUTORIAL_STEPS: "seenTutorialSteps",
}
export class User {
    uid: string;
    name: string;
    email: string | null;
    type: string;
    voicePlans: VoicePlan[];
    playlists: PlaylistReference[];
    featureFlags: string[];
    tagList: string[];
    favoriteTags: string[];
    voicePlanCategories: VoicePlanCategory[];
    usedTags: string[];
    templates: any[]; // Consider creating a Template interface if possible
    lessonTemplates: any[]; // Consider creating a LessonTemplate interface if possible
    expirationDate: Date;
    subscriptionState: string;
    subscriptions: Subscription[];
    entitlements: Entitlement[];
    address: {
        street: string;
        postcode: string;
        city: string;
    };
    migrated: boolean;
    resetPassword: boolean;
    lang: string | null;
    metadata: Record<string, any>;
    emailVerified: boolean;

    constructor(uid: string) {
        this.uid = uid;
        this.name = "";
        this.email = null;
        this.type = "deprecated";
        this.voicePlans = [];
        this.playlists = [];
        this.featureFlags = [];
        this.tagList = [];
        this.favoriteTags = ['my-students', 'my-teachers', 'my-voice-plans'];
        this.voicePlanCategories = [...DEFAULT_VOICE_PLAN_CATEGORIES];
        this.usedTags = [];
        this.templates = [];
        this.lessonTemplates = [];
        this.entitlements = [];
        this.metadata = {};
        this.emailVerified = false;

        this.expirationDate = new Date();
        this.subscriptionState = "";
        this.subscriptions = [];
        this.address = {
            street: "",
            postcode: "",
            city: "",
        };
        this.migrated = false;
        this.lang = null;
        this.resetPassword = false;
    }

    hasFeature(feature: string): boolean {
        if (!this.featureFlags) {
            return false;
        }

        return this.featureFlags.includes(feature);
    }

    hasEntitlement(entitlementKey: string, status: EntitlementStatus): boolean {
        if (!this.entitlements) {
            return false;
        }
        return this.entitlements.some(entitlement =>
            entitlement.key === entitlementKey &&
            (
                (entitlement.status === status)
            )
        );
    }

    getEntitlement(entitlementKey: string): Entitlement | null {
        if (!this.entitlements) {
            return null;
        }
        return this.entitlements.find(entitlement => entitlement.key === entitlementKey) || null;
    }

    isOfType(types: string[]): boolean {
        return types.includes(this.type);
    }

    getNameOfVoicePlan(voicePlanId: string): string {
        return this.voicePlans.find(vp => vp.id === voicePlanId)?.name ?? "Not found";
    }

    /** @deprecated Use voicePlan.canEdit instead. */
    getRoleOfVoicePlan(voicePlanId: string): string {
        return this.voicePlans.find(vp => vp.id === voicePlanId)?.role ?? "student";
    }

    getVoicePlans(filter: (vp: VoicePlan) => boolean = () => true): VoicePlan[] {
        let ids: string[] = [];
        return this.voicePlans.filter(filter).filter(vp => {
            if (ids.includes(vp.id)) {
                return false;
            }
            ids.push(vp.id);
            return true;
        });
    }

    getVoicePlan(id: string): VoicePlan | undefined {
        return this.getVoicePlans((vp) => vp.id === id)[0];
    }

    deleteVoicePlan(id: string): void {
        this.voicePlans = this.voicePlans.filter(vp => vp.id !== id);
    }

    addVoicePlan(voicePlan: VoicePlan): void {
        this.voicePlans.push(voicePlan);
    }

    addPlaylist(playlist: PlaylistReference): void {
        if (!this.playlists.find(p => p.id === playlist.id)) {
            this.playlists.push(playlist);
        }
    }

    removePlaylist(id: string): void {
        this.playlists = this.playlists.filter(p => p.id !== id);
    }

    getPlaylists(): PlaylistReference[] {
        return this.playlists;
    }

    getPlaylist(id: string): PlaylistReference | undefined {
        return this.playlists.find(p => p.id === id);
    }

    hasPlaylist(id: string): boolean {
        return !!this.getPlaylist(id);
    }

    trashVoicePlan(voicePlan: VoicePlan): void {
        const plan = this.getVoicePlan(voicePlan.id);
        if (plan) plan.trashed = true;
    }

    untrashVoicePlan(voicePlan: VoicePlan): void {
        const plan = this.getVoicePlan(voicePlan.id);
        if (plan) plan.trashed = false;
    }

    getTagsList(): string[] {
        return this.tagList;
    }

    addTag(tag: string): void {
        this.tagList.push(tag);
    }

    addLessonTemplate(template: any): void {
        this.lessonTemplates.push(template);
    }

    removeLessonTemplate(template: any): void {
        this.lessonTemplates = this.lessonTemplates.filter(t => t.name !== template.name);
    }

    hasValidSubscription(): boolean {
        return this.subscriptions.some(sub => {
            try {
                return !sub.isExpired();
            } catch {
                return false;
            }
        });
    }

    hasPendingSubscription(): boolean {
        if (!this.subscriptions || this.subscriptions.length === 0) {
            return false;
        }
        // Check if there's a pending subscription and no active subscriptions
        const hasPending = this.subscriptions.some(sub =>
            (sub.metadata as any)?.status === "pending"
        );
        const hasActive = this.hasValidSubscription();
        return hasPending && !hasActive;
    }

    canSubscribe(): boolean {
        return !this.hasValidSubscription() || this.subscriptions.some(sub => sub.isCanceled() && !sub.isExpired());
    }

    addSubscription(subscription: Subscription): void {
        this.subscriptions.push(subscription);
    }

    getMetadata(key: keyof typeof METADATA_KEYS): any {
        if (!this.metadata) {
            this.metadata = {};
        }
        return this.metadata[key];
    }

    setMetadata(key: keyof typeof METADATA_KEYS, value: any): void {
        if (!this.metadata) {
            this.metadata = {};
        }
        this.metadata[key] = value;
    }

    getVoicePlanCategories(): VoicePlanCategory[] {
        return this.voicePlanCategories ?? DEFAULT_VOICE_PLAN_CATEGORIES;
    }

    /** Collect all unique tags used across voice plans (including implicit tags from legacy role). */
    getUsedTags(): string[] {
        const all = new Set<string>();
        for (const vp of this.voicePlans) {
            if (!vp) continue;
            for (const tag of vp.tags) {
                all.add(tag);
            }
        }
        return Array.from(all).sort();
    }

    getLessonTemplates(): any[] {
        return this.lessonTemplates;
    }

    static fromJson(uid: string, json: any): User {
        const user = new User(uid);
        user.name = json.name ?? "";
        user.email = json.email;
        user.type = json.type ?? "deprecated";
        user.voicePlans = json.voicePlans?.filter((vp: any) => vp !== null && vp !== undefined).map((vp: any) => VoicePlan.fromJson(vp)) ?? [];
        user.playlists = json.playlists?.map((p: any) => PlaylistReference.fromJson(p)) ?? [];
        user.featureFlags = json.featureFlags ?? [];
        user.tagList = json.tagList ?? [];
        user.favoriteTags = json.favoriteTags ?? ['my-students', 'my-teachers', 'my-voice-plans'];
        user.voicePlanCategories = json.voicePlanCategories
          ? json.voicePlanCategories.map((c: any) => ({ id: c.id, name: c.name, tags: c.tags ?? [] }))
          : [...DEFAULT_VOICE_PLAN_CATEGORIES];
        user.usedTags = json.usedTags ?? [];
        user.templates = json.templates ?? [];
        user.subscriptions = json.subscriptions?.map((sub: any) => Subscription.fromJson(sub)) ?? [];
        user.entitlements = json.entitlements ?? [];
        user.metadata = json.metadata ?? {};
        user.migrated = !!json.migrated;
        user.address = json.address ?? {
            street: "",
            postcode: "",
            city: "",
        };
        user.lang = json.lang;
        user.lessonTemplates = json.lessonTemplates ?? [];
        user.resetPassword = !!json.resetPassword;
        user.emailVerified = !!json.emailVerified;
        return user;
    }

    toJson(): any {
        return {
            name: this.name,
            email: this.email,
            type: this.type,
            voicePlans: this.voicePlans.map(vp => vp.toJson()),
            playlists: this.playlists.map(p => p.toJson()),
            featureFlags: this.featureFlags,
            tagList: this.tagList,
            favoriteTags: this.favoriteTags,
            voicePlanCategories: this.voicePlanCategories,
            usedTags: this.getUsedTags(),
            templates: this.templates,
            expirationDate: this.expirationDate,
            subscriptions: this.subscriptions.map(sub => sub.toJson()),
            entitlements: this.entitlements,
            metadata: this.metadata,
            migrated: this.migrated,
            address: this.address,
            lang: this.lang,
            lessonTemplates: this.lessonTemplates,
            resetPassword: this.resetPassword,
            emailVerified: this.emailVerified
        };
    }
}
